import { FC } from 'react';
import { TextField } from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import { dateToLabel } from 'further-ui/utils';
import api from 'lib/trpcClient';

interface Props {
  selectedCompanyId: string;
  value?: string;
  onChange: (value: string | undefined) => void;
  error?: boolean;
  helperText?: string;
  disabled?: boolean;
}

const ShareClassSelector: FC<Props> = ({
  selectedCompanyId,
  value,
  onChange,
  error,
  helperText,
  disabled,
}) => {
  const { data: shareClasses, isLoading } =
    api.investee.listShareClasses.useQuery(
      {
        investeeId: selectedCompanyId ?? '',
      },
      {
        enabled: !!selectedCompanyId,
        staleTime: 0,
        refetchOnWindowFocus: true,
      },
    );

  const options =
    shareClasses?.result.map((shareClass) => {
      const shareClassName = shareClass.shareClass || 'Ordinary';
      const lastAllocationDate = shareClass.lastAllocationDate;
      const lastAllocationText = lastAllocationDate
        ? `(Last allocation: ${dateToLabel(lastAllocationDate)})`
        : '';

      return {
        id: shareClass.investeeId,
        label: lastAllocationText
          ? `${shareClassName} ${lastAllocationText}`
          : shareClassName,
      };
    }) || [];

  return (
    <Autocomplete
      options={options}
      getOptionLabel={(option) => option.label || ''}
      getOptionKey={(option) => option.id}
      value={options.find((option) => option.id === value) || null}
      onChange={(_event, selectedOption) => {
        onChange(selectedOption?.id);
      }}
      loading={isLoading}
      disabled={disabled || isLoading || !options.length}
      renderInput={(params) => (
        <TextField
          {...params}
          variant="outlined"
          placeholder={
            isLoading
              ? 'Loading...'
              : !selectedCompanyId
              ? 'First select a company'
              : !options.length
              ? 'No share classes found'
              : 'Select share class'
          }
          error={error}
          helperText={helperText}
          disabled={disabled || isLoading || !options.length}
        />
      )}
    />
  );
};

export default ShareClassSelector;
